import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MaraSportsLogo from '../components/MaraSportsLogo';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
    Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer
} from 'recharts';
import { 
    Search, Users, Star, TrendingUp, Eye, Loader2, Heart, HeartOff,
    Filter, ArrowLeft, BarChart3, Shield, Award, MapPin, UserCheck,
    Zap, Target, ChevronRight, RefreshCw, X, Columns, BookOpen,
    QrCode, Share2, SlidersHorizontal
} from 'lucide-react';
import { scoutingApi, drillLibraryApi, demoApi, videoAnalysisApi } from '../lib/api';
import { toast } from 'sonner';

const ScoutingPortalPage = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [activeTab, setActiveTab] = useState('search');
    const [loading, setLoading] = useState(false);
    
    // Search state
    const [searchResults, setSearchResults] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterSport, setFilterSport] = useState('');
    const [filterMinScore, setFilterMinScore] = useState('');
    const [filterPotential, setFilterPotential] = useState('');
    const [sports, setSports] = useState([]);
    
    // Shortlist
    const [shortlist, setShortlist] = useState([]);
    
    // Comparison
    const [compareIds, setCompareIds] = useState([]);
    const [comparisonData, setComparisonData] = useState(null);
    
    // Recommendations
    const [recommendations, setRecommendations] = useState([]);
    
    // Pipeline
    const [pipeline, setPipeline] = useState(null);
    
    // Demo
    const [demoStatus, setDemoStatus] = useState(null);
    
    // Drill Library
    const [drillLibrary, setDrillLibrary] = useState(null);

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) setUser(JSON.parse(userData));
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            const [sportsRes, demoRes] = await Promise.allSettled([
                videoAnalysisApi.getSupportedSports(),
                demoApi.status(),
            ]);
            if (sportsRes.status === 'fulfilled') setSports(Object.keys(sportsRes.value.data || {}));
            if (demoRes.status === 'fulfilled') setDemoStatus(demoRes.value.data);
        } catch (e) {}
    };

    const handleSearch = async () => {
        setLoading(true);
        try {
            const params = {};
            if (searchQuery) params.search = searchQuery;
            if (filterSport) params.sport = filterSport;
            if (filterMinScore) params.min_score = parseFloat(filterMinScore);
            if (filterPotential) params.potential_rating = filterPotential;
            params.limit = 30;
            
            const res = await scoutingApi.searchAthletes(params);
            setSearchResults(res.data.athletes || []);
        } catch (error) {
            toast.error('Search failed');
        } finally {
            setLoading(false);
        }
    };

    const loadShortlist = async () => {
        if (!user) return;
        try {
            const res = await scoutingApi.getShortlist(user.id);
            setShortlist(res.data.shortlist || []);
        } catch (e) {}
    };

    const addToShortlist = async (athleteId) => {
        if (!user) { toast.error('Login required'); return; }
        try {
            await scoutingApi.addToShortlist({ scout_id: user.id, athlete_id: athleteId, rating: 3 });
            toast.success('Added to shortlist');
            loadShortlist();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed');
        }
    };

    const loadRecommendations = async () => {
        if (!user) return;
        setLoading(true);
        try {
            const res = await scoutingApi.getRecommendations(user.id, filterSport || undefined);
            setRecommendations(res.data.recommendations || []);
        } catch (e) {} finally { setLoading(false); }
    };

    const handleCompare = async () => {
        if (compareIds.length < 2) { toast.error('Select at least 2 athletes'); return; }
        setLoading(true);
        try {
            const res = await scoutingApi.compareAthletes(compareIds);
            setComparisonData(res.data);
        } catch (error) {
            toast.error('Comparison failed');
        } finally { setLoading(false); }
    };

    const loadPipeline = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filterSport) params.sport = filterSport;
            const res = await scoutingApi.getTalentPipeline(params);
            setPipeline(res.data);
        } catch (e) {} finally { setLoading(false); }
    };

    const loadDrillLibrary = async () => {
        setLoading(true);
        try {
            const res = await drillLibraryApi.getLibrary(filterSport || undefined);
            setDrillLibrary(res.data);
        } catch (e) {} finally { setLoading(false); }
    };

    const seedDemo = async () => {
        try {
            const res = await demoApi.seed();
            toast.success('Demo data seeded! Go to Body Tracking to see skeleton overlay.');
            setDemoStatus({ has_demo_data: true, demo_url: res.data.demo_url, demo_video_id: res.data.video_id });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to seed demo');
        }
    };

    const toggleCompare = (athleteId) => {
        setCompareIds(prev => 
            prev.includes(athleteId) ? prev.filter(id => id !== athleteId) : prev.length < 4 ? [...prev, athleteId] : prev
        );
    };

    const ScoreBadge = ({ score }) => (
        <span className={`text-lg font-bold ${score >= 80 ? 'text-green-400' : score >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
            {score?.toFixed(1) || '-'}
        </span>
    );

    const PotentialBadge = ({ rating }) => {
        const colors = {
            'Elite Prospect': 'bg-purple-500/20 text-purple-400',
            'High Potential': 'bg-green-500/20 text-green-400',
            'Developing Talent': 'bg-blue-500/20 text-blue-400',
        };
        return <span className={`px-2 py-0.5 text-xs rounded-full ${colors[rating] || 'bg-gray-500/20 text-gray-400'}`}>{rating || 'N/A'}</span>;
    };

    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            <header className="sticky top-0 z-30 bg-[#0a1628]/80 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 md:px-6">
                    <div className="flex items-center justify-between h-14 md:h-16">
                        <div className="flex items-center gap-2">
                            <Link to="/dashboard" className="flex items-center gap-2">
                                <MaraSportsLogo className="h-8" />
                                <span className="font-heading font-bold text-sm md:text-lg tracking-tight uppercase hidden sm:inline">Scouting Portal</span>
                            </Link>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={seedDemo} className="border-cyan-500/30 text-cyan-400" data-testid="seed-demo-btn">
                                <Zap className="w-4 h-4 mr-1" /> Demo Mode
                            </Button>
                            {demoStatus?.demo_url && (
                                <Link to={demoStatus.demo_url}>
                                    <Button size="sm" variant="outline" className="border-green-500/30 text-green-400" data-testid="view-demo-btn">
                                        <Eye className="w-4 h-4 mr-1" /> View Demo
                                    </Button>
                                </Link>
                            )}
                            <Link to="/dashboard"><Button variant="outline" size="sm" className="border-white/10">Back</Button></Link>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
                <div className="mb-6">
                    <h1 className="font-heading font-bold text-2xl md:text-4xl uppercase tracking-tight mb-2">
                        Talent <span className="text-primary">Scout Hub</span>
                    </h1>
                    <p className="text-muted-foreground text-sm">Search, compare, and shortlist athletes across all 20 sports.</p>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                    {[
                        { id: 'search', icon: Search, label: 'Search Athletes' },
                        { id: 'shortlist', icon: Heart, label: 'Shortlist', action: loadShortlist },
                        { id: 'compare', icon: Columns, label: `Compare (${compareIds.length})` },
                        { id: 'recommend', icon: Zap, label: 'AI Recommendations', action: loadRecommendations },
                        { id: 'pipeline', icon: TrendingUp, label: 'Talent Pipeline', action: loadPipeline },
                        { id: 'drills', icon: BookOpen, label: 'Drill Library', action: loadDrillLibrary },
                    ].map(tab => (
                        <Button key={tab.id} size="sm"
                            variant={activeTab === tab.id ? 'default' : 'outline'}
                            onClick={() => { setActiveTab(tab.id); tab.action?.(); }}
                            className={activeTab !== tab.id ? 'border-white/10' : ''}
                            data-testid={`tab-${tab.id}`}
                        >
                            <tab.icon className="w-4 h-4 mr-1" />{tab.label}
                        </Button>
                    ))}
                </div>

                {/* Search Tab */}
                {activeTab === 'search' && (
                    <div className="space-y-4">
                        <Card className="bg-[#0d2847] border-white/5">
                            <CardContent className="p-4">
                                <div className="flex flex-wrap gap-3">
                                    <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by name..."
                                        className="flex-1 min-w-[200px] bg-[#0a1628] border border-white/10 rounded-lg px-4 py-2 text-sm" data-testid="search-input"
                                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                    />
                                    <select value={filterSport} onChange={(e) => setFilterSport(e.target.value)} className="bg-[#0a1628] border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="filter-sport">
                                        <option value="">All Sports</option>
                                        {sports.sort().map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                    <input type="number" value={filterMinScore} onChange={(e) => setFilterMinScore(e.target.value)} placeholder="Min Score"
                                        className="w-28 bg-[#0a1628] border border-white/10 rounded-lg px-3 py-2 text-sm" />
                                    <select value={filterPotential} onChange={(e) => setFilterPotential(e.target.value)} className="bg-[#0a1628] border border-white/10 rounded-lg px-3 py-2 text-sm">
                                        <option value="">All Potential</option>
                                        <option value="Elite Prospect">Elite Prospect</option>
                                        <option value="High Potential">High Potential</option>
                                        <option value="Developing Talent">Developing Talent</option>
                                    </select>
                                    <Button onClick={handleSearch} disabled={loading} data-testid="search-btn">
                                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-1" />} Search
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {searchResults.length > 0 ? (
                            <div className="space-y-3">
                                <p className="text-sm text-muted-foreground">{searchResults.length} athletes found</p>
                                {searchResults.map((athlete, i) => (
                                    <Card key={i} className="bg-[#0d2847] border-white/5 hover:border-primary/20 transition-colors" data-testid={`result-${i}`}>
                                        <CardContent className="p-4">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                                                    <Users className="w-5 h-5 text-primary" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <p className="font-medium">{athlete.athlete_name}</p>
                                                        <span className="text-xs text-primary bg-primary/10 px-2 py-0.5 rounded">{athlete.sport}</span>
                                                        {athlete.position && <span className="text-xs text-muted-foreground">{athlete.position}</span>}
                                                        <PotentialBadge rating={athlete.potential_rating} />
                                                    </div>
                                                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                                                        {athlete.county && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{athlete.county}</span>}
                                                        <span>{athlete.total_videos} videos</span>
                                                    </div>
                                                </div>
                                                <ScoreBadge score={athlete.best_score} />
                                                <div className="flex gap-1">
                                                    <Button size="sm" variant="ghost" onClick={() => addToShortlist(athlete.athlete_id)} title="Add to shortlist" data-testid={`shortlist-${i}`}>
                                                        <Heart className="w-4 h-4" />
                                                    </Button>
                                                    <Button size="sm" variant={compareIds.includes(athlete.athlete_id) ? 'default' : 'ghost'}
                                                        onClick={() => toggleCompare(athlete.athlete_id)} title="Compare" data-testid={`compare-toggle-${i}`}>
                                                        <Columns className="w-4 h-4" />
                                                    </Button>
                                                    {athlete.latest_video_id && (
                                                        <Link to={`/video-analysis/${athlete.latest_video_id}`}>
                                                            <Button size="sm" variant="ghost"><Eye className="w-4 h-4" /></Button>
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : !loading && (
                            <Card className="bg-[#0d2847] border-white/5">
                                <CardContent className="py-12 text-center">
                                    <Search className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                                    <p className="text-muted-foreground">Search for athletes to begin scouting</p>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                )}

                {/* Shortlist Tab */}
                {activeTab === 'shortlist' && (
                    <div className="space-y-3">
                        {shortlist.length === 0 ? (
                            <Card className="bg-[#0d2847] border-white/5"><CardContent className="py-12 text-center">
                                <Heart className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                                <p className="text-muted-foreground">Your shortlist is empty. Search and add athletes.</p>
                            </CardContent></Card>
                        ) : shortlist.map((item, i) => (
                            <Card key={i} className="bg-[#0d2847] border-white/5" data-testid={`shortlist-item-${i}`}>
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-4">
                                        <div className="flex gap-0.5">{[1,2,3,4,5].map(s => (
                                            <Star key={s} className={`w-4 h-4 ${s <= (item.rating || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`} />
                                        ))}</div>
                                        <div className="flex-1">
                                            <p className="font-medium">{item.athlete_name}</p>
                                            <p className="text-xs text-muted-foreground">{item.sport} {item.county ? `| ${item.county}` : ''}</p>
                                        </div>
                                        <ScoreBadge score={item.best_score} />
                                        <PotentialBadge rating={item.potential_rating} />
                                        <Button size="sm" variant="ghost" onClick={async () => { await scoutingApi.removeFromShortlist(item.id); loadShortlist(); toast.success('Removed'); }}>
                                            <X className="w-4 h-4 text-red-400" />
                                        </Button>
                                    </div>
                                    {item.notes && <p className="text-xs text-muted-foreground mt-2 pl-24">{item.notes}</p>}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Compare Tab */}
                {activeTab === 'compare' && (
                    <div className="space-y-4">
                        <Card className="bg-[#0d2847] border-white/5">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm">{compareIds.length} athletes selected for comparison</p>
                                    <div className="flex gap-2">
                                        <Button size="sm" variant="outline" className="border-white/10" onClick={() => setCompareIds([])}><X className="w-3 h-3 mr-1" /> Clear</Button>
                                        <Button size="sm" onClick={handleCompare} disabled={compareIds.length < 2 || loading} data-testid="compare-btn">
                                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Columns className="w-4 h-4 mr-1" />} Compare
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {comparisonData && (
                            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${comparisonData.comparison.length}, 1fr)` }}>
                                {comparisonData.comparison.map((athlete, i) => (
                                    <Card key={i} className="bg-[#0d2847] border-white/5" data-testid={`compare-card-${i}`}>
                                        <CardContent className="p-4 space-y-3">
                                            <div className="text-center">
                                                <p className="font-bold">{athlete.name}</p>
                                                <p className="text-xs text-primary">{athlete.sport} {athlete.position ? `| ${athlete.position}` : ''}</p>
                                                {athlete.county && <p className="text-xs text-muted-foreground"><MapPin className="w-3 h-3 inline" /> {athlete.county}</p>}
                                            </div>
                                            <div className="text-center p-3 bg-[#0a1628] rounded-lg">
                                                <p className="text-3xl font-bold text-primary">{athlete.overall_score || '-'}</p>
                                                <p className="text-xs text-muted-foreground">Score</p>
                                            </div>
                                            <div className="text-center">
                                                <PotentialBadge rating={athlete.potential_rating} />
                                            </div>
                                            {athlete.metrics && (
                                                <ResponsiveContainer width="100%" height={180}>
                                                    <RadarChart data={Object.entries(athlete.metrics).map(([k, v]) => ({ metric: k.length > 10 ? k.substring(0,10) + '..' : k, value: v }))}>
                                                        <PolarGrid stroke="rgba(255,255,255,0.1)" />
                                                        <PolarAngleAxis dataKey="metric" tick={{ fill: '#94a3b8', fontSize: 8 }} />
                                                        <PolarRadiusAxis domain={[0, 100]} tick={false} />
                                                        <Radar dataKey="value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
                                                    </RadarChart>
                                                </ResponsiveContainer>
                                            )}
                                            {athlete.strengths?.slice(0, 2).map((s, j) => (
                                                <p key={j} className="text-xs text-green-400">+ {s}</p>
                                            ))}
                                            {athlete.latest_video_id && (
                                                <Link to={`/video-analysis/${athlete.video_id}`}>
                                                    <Button size="sm" variant="outline" className="w-full border-white/10"><Eye className="w-3 h-3 mr-1" /> View Analysis</Button>
                                                </Link>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* AI Recommendations Tab */}
                {activeTab === 'recommend' && (
                    <div className="space-y-3">
                        {recommendations.length === 0 ? (
                            <Card className="bg-[#0d2847] border-white/5"><CardContent className="py-12 text-center">
                                <Zap className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                                <p className="text-muted-foreground">AI recommendations based on your scouting patterns</p>
                                <Button className="mt-4" onClick={loadRecommendations}>Load Recommendations</Button>
                            </CardContent></Card>
                        ) : recommendations.map((rec, i) => (
                            <Card key={i} className="bg-[#0d2847] border-white/5" data-testid={`rec-${i}`}>
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-4">
                                        <Zap className="w-5 h-5 text-yellow-400" />
                                        <div className="flex-1">
                                            <p className="font-medium">{rec.athlete_name}</p>
                                            <p className="text-xs text-muted-foreground">{rec.sport} {rec.position ? `| ${rec.position}` : ''}</p>
                                        </div>
                                        <ScoreBadge score={rec.score} />
                                        <PotentialBadge rating={rec.potential_rating} />
                                        <Button size="sm" variant="ghost" onClick={() => addToShortlist(rec.athlete_id)}><Heart className="w-4 h-4" /></Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Talent Pipeline Tab */}
                {activeTab === 'pipeline' && (
                    <div className="space-y-4">
                        {pipeline ? (
                            <>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <Card className="bg-[#0d2847] border-white/5"><CardContent className="p-4 text-center">
                                        <p className="text-2xl font-bold text-primary">{pipeline.total_talent}</p>
                                        <p className="text-xs text-muted-foreground">Total Talent Pool</p>
                                    </CardContent></Card>
                                    <Card className="bg-[#0d2847] border-white/5"><CardContent className="p-4 text-center">
                                        <p className="text-2xl font-bold text-purple-400">{pipeline.summary?.by_tier?.['Elite Prospect'] || 0}</p>
                                        <p className="text-xs text-muted-foreground">Elite Prospects</p>
                                    </CardContent></Card>
                                    <Card className="bg-[#0d2847] border-white/5"><CardContent className="p-4 text-center">
                                        <p className="text-2xl font-bold text-green-400">{pipeline.summary?.by_tier?.['High Potential'] || 0}</p>
                                        <p className="text-xs text-muted-foreground">High Potential</p>
                                    </CardContent></Card>
                                    <Card className="bg-[#0d2847] border-white/5"><CardContent className="p-4 text-center">
                                        <p className="text-2xl font-bold text-yellow-400">{pipeline.summary?.avg_score || 0}</p>
                                        <p className="text-xs text-muted-foreground">Avg Score</p>
                                    </CardContent></Card>
                                </div>
                                
                                {pipeline.summary?.by_sport && (
                                    <Card className="bg-[#0d2847] border-white/5">
                                        <CardHeader><CardTitle className="text-sm">Athletes by Sport</CardTitle></CardHeader>
                                        <CardContent>
                                            <div className="flex flex-wrap gap-2">
                                                {Object.entries(pipeline.summary.by_sport).sort((a, b) => b[1] - a[1]).map(([sport, count]) => (
                                                    <span key={sport} className="px-3 py-1.5 bg-[#0a1628] rounded-lg text-xs">
                                                        {sport}: <strong className="text-primary">{count}</strong>
                                                    </span>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}

                                <Card className="bg-[#0d2847] border-white/5">
                                    <CardHeader><CardTitle className="text-sm">Top Talent</CardTitle></CardHeader>
                                    <CardContent>
                                        <div className="space-y-2">
                                            {pipeline.pipeline?.slice(0, 15).map((a, i) => (
                                                <div key={i} className="flex items-center gap-3 p-2 bg-[#0a1628] rounded text-sm" data-testid={`pipeline-athlete-${i}`}>
                                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                                        i < 3 ? 'bg-yellow-500 text-black' : 'bg-white/10'
                                                    }`}>{i + 1}</span>
                                                    <span className="flex-1 font-medium">{a.name}</span>
                                                    <span className="text-xs text-primary">{a.sport}</span>
                                                    <span className="text-xs text-muted-foreground">{a.county || '-'}</span>
                                                    <span className="font-bold text-primary">{a.best_score}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            </>
                        ) : (
                            <Card className="bg-[#0d2847] border-white/5"><CardContent className="py-12 text-center">
                                <TrendingUp className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                                <p className="text-muted-foreground">Federation talent pipeline report</p>
                                <Button className="mt-4" onClick={loadPipeline}>Load Pipeline</Button>
                            </CardContent></Card>
                        )}
                    </div>
                )}

                {/* Drill Library Tab */}
                {activeTab === 'drills' && (
                    <div className="space-y-4">
                        <Card className="bg-[#0d2847] border-white/5">
                            <CardContent className="p-4">
                                <div className="flex gap-3">
                                    <select value={filterSport} onChange={(e) => setFilterSport(e.target.value)} className="bg-[#0a1628] border border-white/10 rounded-lg px-3 py-2 text-sm">
                                        <option value="">All Sports</option>
                                        {sports.sort().map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                    <Button onClick={loadDrillLibrary} data-testid="load-drills-btn">
                                        <BookOpen className="w-4 h-4 mr-1" /> Load Drills
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {drillLibrary && (
                            <div className="space-y-4">
                                <p className="text-sm text-muted-foreground">{drillLibrary.total || drillLibrary.total_drills} drills across {drillLibrary.total_sports || 1} sports</p>
                                {Object.entries(drillLibrary.sports || (drillLibrary.sport ? {[drillLibrary.sport]: drillLibrary.drills} : {})).map(([sport, drills]) => (
                                    <Card key={sport} className="bg-[#0d2847] border-white/5">
                                        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BookOpen className="w-4 h-4 text-primary" />{sport} ({drills.length} drills)</CardTitle></CardHeader>
                                        <CardContent>
                                            <div className="grid gap-2 md:grid-cols-2">
                                                {drills.map((drill, i) => (
                                                    <div key={i} className="p-3 bg-[#0a1628] rounded-lg" data-testid={`drill-${drill.id}`}>
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className="font-medium text-sm">{drill.name}</span>
                                                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                                                                drill.difficulty === 'Beginner' ? 'bg-green-500/20 text-green-400' :
                                                                drill.difficulty === 'Intermediate' ? 'bg-yellow-500/20 text-yellow-400' :
                                                                'bg-red-500/20 text-red-400'
                                                            }`}>{drill.difficulty}</span>
                                                        </div>
                                                        <p className="text-xs text-muted-foreground">{drill.description}</p>
                                                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                                            <span>{drill.duration_min} min</span>
                                                            <span>{drill.reps} reps</span>
                                                            <span className="text-primary">{drill.category}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
};

export default ScoutingPortalPage;
